/* eslint-disable @typescript-eslint/ban-ts-comment */
import { IconButton } from '@mui/material';
import { Loader } from '@treedis/tour-loader';
import Player from '@vimeo/player';
import classnames from 'classnames';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useBasicStore } from '../../../../store';
import DeviceManager from '../../../util/deviceManager';
import { getPlayerType, getVimeoId, getYoukuId, getYoutubeId } from './data';
import { PlayerType, Props } from './types';

const PERCENT_AFTER_START_EVENT = 0.7;

const LoaderWrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const MainWrap = styled.div`
  .cs-introWrap {
    height: 100%;
  }
  #intro-clip-iframe {
    background: black;
  }
`;
const ClipScreen: FC<Props & { children?: any }> = (props) => {
  const {
    videoUrl,
    close,
    disableAutoplay,
    hideBtnGroup,
    showControls,
    noMobile,
    subtitles,
    onStartEvent,
    autoClose = true,
    hideSkipBtn,
  } = props;
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(false);
  const [type, setType] = useState(PlayerType.NOTSET);
  const translation = useBasicStore((state) => state.translationData);
  const player = useRef(null);
  const isEventStarted = useRef(false);
  const isMobile = DeviceManager.isMobile();
  const [showPlayBtn, setShowPlayBtn] = useState(false);

  const subtitlesCustomReplace = (subCode: string) => {
    // vimeo only
    // vimeo have different language codes (non iso) for some languages
    // for more check
    // https://vimeo.zendesk.com/hc/en-us/articles/224968828-Captions-and-subtitles#h_01FH9P8GQJ1ERMDZB2DRHJKAR0

    const subsToReplace = {
      he: 'iw',
    };
    return subsToReplace[subCode] || subCode;
  };

  const loadVimeoVideo = useCallback(() => {
    const playerVars: any = {
      id: getVimeoId(videoUrl),
      autoplay: !disableAutoplay && !DeviceManager.isIOS(),
      controls: !!showControls || DeviceManager.isIOS(),
      dnt: true,
    };
    if (subtitles) playerVars.texttrack = subtitlesCustomReplace(subtitles);
    const introClipBlock = document.querySelector('#intro-clip-iframe');
    player.current = new Player(introClipBlock, playerVars);
    player.current.on('timeupdate', (data) => {
      if (onStartEvent && data.percent >= PERCENT_AFTER_START_EVENT && !isEventStarted.current) {
        onStartEvent();
        isEventStarted.current = true;
      }
    });
    player.current.on('loaded', () => {
      setLoading(false);
      if (disableAutoplay) return;
      player.current.play().catch(() => {
        // nothing
      });
    });
    if (close) player.current.on('ended', () => autoClose && close());
  }, [videoUrl, disableAutoplay, showControls, subtitles, close, onStartEvent, autoClose]);

  const loadYKUVideo = useCallback(() => {
    const container = window.document.getElementById('intro-clip-iframe');
    const iframe = document.createElement('iframe');
    iframe.setAttribute(
      'src',
      `https://player.youku.com/embed/${getYoukuId(videoUrl)}?autoplay=1&controls=0&rel=0&showinfo=0`,
    );
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    setTimeout(() => setLoading(false), 2000);
    container.appendChild(iframe);
  }, [videoUrl]);

  const loadYTVideo = useCallback(() => {
    // @ts-ignore

    const playerVars: any = {
      autoplay: disableAutoplay ? 0 : 1,
      controls: showControls ? 1 : 0,
      rel: 0,
      showinfo: 0,
      disablekb: 1,
      fs: 0,
      modestbranding: 1,
    };
    if (subtitles) playerVars.hl = subtitles;
    // @ts-ignore
    player.current = new window.YT.Player('intro-clip-iframe', {
      videoId: getYoutubeId(videoUrl),
      playerVars,
      events: {
        onStateChange: (event) => {
          // @ts-ignore
          if (event.data === window.YT.PlayerState.ENDED && close) {
            if (autoClose) close();
          }
        },
        onReady: (event) => {
          setLoading(false);
          if (disableAutoplay) return;
          event.target.playVideo();
        },
      },
    });
  }, [disableAutoplay, showControls, subtitles, videoUrl, close, autoClose]);

  const loadNativeVideo = useCallback(() => {
    if (videoUrl) {
      const videoElement = document.createElement('video');
      // create source element with type mp4
      const sourceElement = document.createElement('source');
      sourceElement.type = 'video/mp4';
      sourceElement.src = videoUrl;
      videoElement.appendChild(sourceElement);
      videoElement.controls = false;
      videoElement.autoplay = !disableAutoplay;
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', '');
      let autoplaySuccessful = false;

      videoElement.addEventListener('play', () => {
        autoplaySuccessful = true;
      });

      videoElement.addEventListener('canplaythrough', () => {
        setLoading(false);

        if (!autoplaySuccessful) {
          setShowPlayBtn(true);
        }
      });
      videoElement.addEventListener('ended', () => {
        if (autoClose) {
          close();
        }
      });
      videoElement.addEventListener('click', () => {
        videoElement.play();
        setShowPlayBtn(false);
      });
      player.current = videoElement;
      document.getElementById('intro-clip-iframe').appendChild(videoElement);
    }
  }, [autoClose, close, disableAutoplay, videoUrl]);

  useEffect(() => {
    // should load video once
    if (!videoUrl || type !== PlayerType.NOTSET) return;
    const newType = getPlayerType(videoUrl);

    switch (newType) {
      case PlayerType.VIMEO:
        loadVimeoVideo();
        break;
      case PlayerType.YOUTUBE:
        // @ts-ignore
        if (!window.YT) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          // @ts-ignore
          window.onYouTubeIframeAPIReady = loadYTVideo;
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } else {
          loadYTVideo();
        }
        break;
      case PlayerType.YOUKU:
        loadYKUVideo();
        break;
      default:
        loadNativeVideo();
        break;
    }
    setType(newType);
  }, [loadNativeVideo, loadVimeoVideo, loadYKUVideo, loadYTVideo, type, videoUrl]);

  const mute = useCallback(() => {
    if (!player.current) return;
    switch (type) {
      case PlayerType.VIMEO:
        player.current.setMuted(true);
        break;
      case PlayerType.YOUTUBE:
        player.current.mute();
        break;
      default:
        player.current.muted = true;
        break;
    }
    setMuted(true);
  }, [type]);

  const unMute = useCallback(() => {
    if (!player.current) return;
    switch (type) {
      case PlayerType.VIMEO:
        player.current.setMuted(false);
        break;
      case PlayerType.YOUTUBE:
        player.current.unMute();
        break;
      default:
        player.current.muted = false;
        break;
    }
    setMuted(false);
  }, [type]);

  return (
    <MainWrap id="intro" className={classnames('intro', DeviceManager.isMobile() && 'mobile')}>
      {loading && (
        <LoaderWrap>
          <Loader />
        </LoaderWrap>
      )}
      <div style={{ display: loading ? 'none' : 'initial' }} className="cs-introWrap">
        {!hideBtnGroup && (
          <div className="intro-btn-group">
            {type !== PlayerType.YOUKU && (
              <button className="intro-button" onClick={muted ? unMute : mute}>
                <i className={`fas ${muted ? 'fa-volume-slash' : 'fa-volume'}`} />
              </button>
            )}
            {!hideSkipBtn && (
              <button className="intro-button" onClick={close || (() => null)}>
                <i>{translation?.skip || 'Skip'}</i>
              </button>
            )}
          </div>
        )}
        <div id="intro-clip-iframe" className={classnames(!noMobile && isMobile && 'mobile')} />
      </div>
      {showPlayBtn && (
        <IconButton
          sx={{
            position: 'fixed',
            top: '45%',
            left: '50%',
            zIndex: 5,
            color: 'rgba(0,0,0,0.8)',
            transform: 'translateX(-50%)',

            '&>i': {
              fontSize: '7vw !important',
            },
          }}
          onClick={() => {
            player.current?.play();
            setShowPlayBtn(false);
          }}
        >
          <i className="fa-solid fa-circle-play" />
        </IconButton>
      )}
    </MainWrap>
  );
};

function areEqual(prevProps: any, nextProps: any) {
  return prevProps.videoUrl !== nextProps.videoUrl;
}

export default memo(ClipScreen, areEqual);
